const BMSM_EVENT_NAMES = new Set([
  'bmsm_offer_viewed',
  'bmsm_offer_clicked',
  'bmsm_offer_added_to_cart',
]);
const BMSM_DEFAULT_SOURCE = 'pdp_pill';
const viewedOffers = new Set();
const pendingVisiblePills = new Set();
let impressionObserver = null;

function analyticsAllowed() {
  const customerPrivacy = window.Shopify?.customerPrivacy;
  return typeof customerPrivacy?.analyticsProcessingAllowed === 'function'
    && customerPrivacy.analyticsProcessingAllowed();
}

function positiveInteger(value) {
  const number = Number.parseInt(value, 10);
  return Number.isInteger(number) && number > 0 ? number : null;
}

function finiteNumber(value) {
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

/**
 * Build the shared BMSM event payload.
 *
 * @param {string} eventName
 * @param {Object} options
 * @returns {Object|null}
 */
export function bmsmEventPayload(eventName, options = {}) {
  const offerId = String(options.offerId || '').trim();
  const productId = String(options.productId || '').trim();
  const variantId = String(options.variantId || '').trim();
  const quantity = positiveInteger(options.quantity);

  if (!BMSM_EVENT_NAMES.has(eventName) || !offerId || !productId || !variantId || !quantity) {
    return null;
  }

  const discountPercent = finiteNumber(options.discountPercent);
  const valueCents = finiteNumber(options.valueCents);
  const payload = {
    event: eventName,
    bmsm_offer_id: offerId,
    bmsm_source: String(options.source || BMSM_DEFAULT_SOURCE),
    item_id: productId,
    variant_id: variantId,
    quantity,
  };

  if (options.productName) payload.item_name = String(options.productName);
  if (discountPercent !== null) payload.discount_percent = discountPercent;
  if (valueCents !== null && valueCents >= 0) payload.value = valueCents / 100;
  if (options.currency) payload.currency = String(options.currency).toUpperCase();

  return payload;
}

/**
 * Push a consented BMSM event to the existing GTM data layer.
 *
 * @param {string} eventName
 * @param {Object} options
 * @returns {boolean}
 */
export function trackBmsmEvent(eventName, options = {}) {
  if (!analyticsAllowed()) return false;

  const payload = bmsmEventPayload(eventName, options);
  if (!payload) return false;

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(payload);
  return true;
}

function pillOptions(pill) {
  const group = pill.closest('.product-form__buttons-group');
  const quantity = positiveInteger(pill.dataset.bmsmQty);
  const unitPriceCents = finiteNumber(pill.dataset.unitPriceCents);
  const discount = finiteNumber(pill.dataset.bmsmDiscount);

  return {
    offerId: pill.dataset.bmsmOfferId,
    source: BMSM_DEFAULT_SOURCE,
    productId: group?.dataset.productId,
    productName: group?.dataset.productTitle,
    variantId: pill.dataset.variantId,
    quantity,
    discountPercent: discount === null ? null : discount * 100,
    valueCents: unitPriceCents === null || quantity === null ? null : unitPriceCents * quantity,
    currency: group?.dataset.currencyCode,
  };
}

function pillIsVisible(pill) {
  return !pill.hidden && pill.style.display !== 'none';
}

function offerViewKey(options) {
  return `${options.offerId || ''}:${options.variantId || ''}`;
}

function trackVisiblePill(pill) {
  if (!pillIsVisible(pill)) return false;

  const options = pillOptions(pill);
  const viewKey = offerViewKey(options);
  if (viewedOffers.has(viewKey)) return false;

  const tracked = trackBmsmEvent('bmsm_offer_viewed', options);
  if (tracked) viewedOffers.add(viewKey);
  return tracked;
}

function observePillImpression(pill) {
  const options = pillOptions(pill);
  if (!pillIsVisible(pill) || viewedOffers.has(offerViewKey(options))) return;

  if (typeof window.IntersectionObserver !== 'function') {
    if (!trackVisiblePill(pill)) pendingVisiblePills.add(pill);
    return;
  }

  if (!impressionObserver) {
    impressionObserver = new window.IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) {
          pendingVisiblePills.delete(entry.target);
          continue;
        }

        const tracked = trackVisiblePill(entry.target);
        if (!tracked && !viewedOffers.has(offerViewKey(pillOptions(entry.target)))) {
          pendingVisiblePills.add(entry.target);
          continue;
        }

        pendingVisiblePills.delete(entry.target);
        impressionObserver.unobserve(entry.target);
      }
    }, { threshold: 0.25 });
  }

  impressionObserver.observe(pill);
}

function scanVisiblePills(root = document) {
  const scope = root && typeof root.querySelectorAll === 'function' ? root : document;
  const pills = scope.querySelectorAll('.bmsm-pill[data-bmsm-offer-id]');

  for (const pill of pills) {
    observePillImpression(pill);
  }
}

function retryPendingImpressions() {
  for (const pill of pendingVisiblePills) {
    if (!pillIsVisible(pill)) {
      pendingVisiblePills.delete(pill);
      impressionObserver?.unobserve(pill);
      continue;
    }

    if (!trackVisiblePill(pill)) continue;

    pendingVisiblePills.delete(pill);
    impressionObserver?.unobserve(pill);
  }

  scanVisiblePills(document);
}

function stopObservingPills(root) {
  if (!root || typeof root.querySelectorAll !== 'function') return;

  const pills = root.querySelectorAll('.bmsm-pill[data-bmsm-offer-id]');
  for (const pill of pills) {
    pendingVisiblePills.delete(pill);
    impressionObserver?.unobserve(pill);
  }
}

function handleClick(event) {
  const pill = event.target?.closest?.('.bmsm-pill[data-bmsm-offer-id]');
  if (!pill) return;

  trackVisiblePill(pill);
  trackBmsmEvent('bmsm_offer_clicked', pillOptions(pill));
}

function addedItem(event) {
  const eventDetail = event.detail || {};
  const response = eventDetail.product || eventDetail;
  const item = Array.isArray(response.items) ? response.items[0] || {} : response;
  const properties = item.properties || {};

  if (!properties._bmsm_offer_id) return null;

  const requestedQuantity = positiveInteger(eventDetail.bmsmRequest?.quantity);
  const requestedValue = eventDetail.bmsmRequest?.valueCents;
  const requestedValueCents = requestedValue == null ? null : finiteNumber(requestedValue);
  const quantity = requestedQuantity || positiveInteger(item.quantity);
  const unitPriceCents = finiteNumber(item.final_price ?? item.price);
  const lineValueCents = finiteNumber(item.final_line_price ?? item.line_price);
  const responseValueCents = lineValueCents === null && unitPriceCents !== null && quantity !== null
    ? unitPriceCents * quantity
    : lineValueCents;

  return {
    offerId: properties._bmsm_offer_id,
    source: properties._bmsm_source || BMSM_DEFAULT_SOURCE,
    productId: item.product_id,
    productName: item.product_title || item.title,
    variantId: item.variant_id || item.id,
    quantity,
    discountPercent: properties._bmsm_discount_percent,
    valueCents: requestedValueCents ?? responseValueCents,
  };
}

function currencyForProduct(productId) {
  const groups = document.querySelectorAll('.product-form__buttons-group[data-product-id]');

  for (const group of groups) {
    if (String(group.dataset.productId) === String(productId)) {
      return group.dataset.currencyCode || '';
    }
  }

  return '';
}

function handleProductAdded(event) {
  const options = addedItem(event);
  if (!options) return;

  options.currency = currencyForProduct(options.productId);
  trackBmsmEvent('bmsm_offer_added_to_cart', options);
}

function boot(root) {
  scanVisiblePills(root);
}

document.addEventListener('click', handleClick);
document.addEventListener('ajaxProduct:added', handleProductAdded);
document.addEventListener('shopifyCustomerPrivacyApiLoaded', retryPendingImpressions);
document.addEventListener('visitorConsentCollected', retryPendingImpressions);
document.addEventListener('bmsm:offers-updated', (event) => boot(event.target));
document.addEventListener('shopify:section:load', (event) => boot(event.target));
document.addEventListener('shopify:section:unload', (event) => stopObservingPills(event.target));

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => boot(document), { once: true });
} else {
  boot(document);
}
